import { TaskStage } from '../types/Task';

/**
 * The task lifecycle, in the order work actually moves through it. Status pickers must present
 * options in this order — the API returns statuses in whatever order the query produced, which is
 * meaningless to a user scanning for "everything past Internal Review".
 *
 * CANCELLED sits outside the progression (a task can be cancelled from anywhere), so it sorts last.
 */
export const TASK_STATUS_SEQUENCE: string[] = [
  TaskStage.PROPOSED,
  TaskStage.PLANNED,
  TaskStage.ACTIVE,
  TaskStage.ACTIVE_MARKED_AS_COMPLETE,
  TaskStage.INTERNAL_REVIEW,
  TaskStage.CLOSED,
  TaskStage.FINALIZED,
  TaskStage.CANCELLED,
];

/**
 * Orders arbitrary status strings by TASK_STATUS_SEQUENCE. Unknown values keep their relative
 * order and sort after the known ones rather than being dropped.
 */
export const sortTaskStatuses = <T>(items: T[], getStatus: (item: T) => string): T[] => {
  const rank = (s: string) => {
    const i = TASK_STATUS_SEQUENCE.indexOf(s?.toUpperCase() || '');
    return i === -1 ? TASK_STATUS_SEQUENCE.length : i;
  };
  return [...items]
    .map((item, i) => ({ item, i, r: rank(getStatus(item)) }))
    .sort((a, b) => a.r - b.r || a.i - b.i)
    .map((x) => x.item);
};

/**
 * Formats a task status enum value to a user-friendly display string
 * @param status - The TaskStage enum value
 * @returns A user-friendly formatted status string
 */
export const formatStatusForDisplay = (status: string): string => {
  // Convert status to uppercase for lookup to handle both lowercase and uppercase inputs
  const statusUpper = status?.toUpperCase() || '';
  
  const statusMap: Record<string, string> = {
    [TaskStage.PROPOSED]: 'Proposed',
    [TaskStage.PLANNED]: 'Planned',
    [TaskStage.ACTIVE]: 'Active',
    [TaskStage.ACTIVE_MARKED_AS_COMPLETE]: 'Active - Marked as Complete',
    [TaskStage.INTERNAL_REVIEW]: 'Internal Review',
    [TaskStage.CLOSED]: 'Closed',
    [TaskStage.FINALIZED]: 'Finalized',
    [TaskStage.CANCELLED]: 'Cancelled',
    // Add fallback for other status values (non-TaskStage enums)
    'INACTIVE': 'Inactive',

    // History stages. These reach the same badge as a task status (task, Work ID, PCR and
    // project history all render through StatusBadge), and without an entry here they printed
    // the raw database value — "effort_applied" in a badge on the task history panel. Colours
    // for these already existed in STATUS_VARIANTS; only the wording was missing.
    'EFFORT_APPLIED': 'Effort Applied',
    'MANUAL_COMMENT': 'Comment',
    'CREATED': 'Created',
    'REPORTED': 'Reported',
    'REOPENED': 'Reopened',
    'DATA_EDIT': 'Data Edit',
    'STATUS_CHANGE': 'Status Change',
    'APPROVAL': 'Approval',
    'ASSIGNMENT': 'Assignment',
    'CANCELLATION': 'Cancellation',
    'MERGED': 'Merged',
    'CLOSED_WITHOUT_ACTION': 'Closed Without Action',
    'PROMOTED_TO_WORK_ID': 'Promoted to Work ID',
    'CLOSED_WITHOUT_WORK_ID': 'Closed Without Work ID',
    'OUTBOUND_EMAIL': 'Outbound Email',
    'TASK_CREATED': 'Task Created',
    'TASK_CANCELLED': 'Task Cancelled',
    'RESOURCE_ADDED': 'Resource added',
    'RESOURCE_REMOVED': 'Resource removed',
    'RATE_ENTERED': 'Rate entered',
    'RATE_REQUESTED': 'Rate requested',
    'RATE_APPROVED': 'Rate approved',
    'RATE_REJECTED': 'Rate rejected',
    'RATE_CANCELLED': 'Rate cancelled',
    'RATE_WINDOW_UPDATED': 'Rate window updated',
    'DOCUMENT_ADDED': 'Document uploaded',
    'DOCUMENT_UPDATED': 'Document updated',
    'DOCUMENT_REMOVED': 'Document removed',
    'AREA_ADDED': 'Added to area',
    'AVAILABILITY_CHANGE': 'Availability changed',
    'INSURANCE_UPLOADED': 'Vehicle insurance uploaded',
    'INSURANCE_CLEARED': 'Vehicle insurance removed',
  };

  return statusMap[statusUpper] || status;
};

/**
 * Maps from a user-friendly status display string back to the enum value
 * @param displayStatus - The user-friendly status string
 * @returns The TaskStage enum value
 */
export const formatStatusToEnum = (displayStatus: string): string => {
  const reverseMap: Record<string, string> = {
    'Proposed': TaskStage.PROPOSED,
    'Planned': TaskStage.PLANNED,
    'Active': TaskStage.ACTIVE,
    'Active - Marked as Complete': TaskStage.ACTIVE_MARKED_AS_COMPLETE,
    'Internal Review': TaskStage.INTERNAL_REVIEW,
    'Closed': TaskStage.CLOSED,
    'Finalized': TaskStage.FINALIZED,
    'Cancelled': TaskStage.CANCELLED,
    'Inactive': 'INACTIVE',
  };

  return reverseMap[displayStatus] || displayStatus;
};

/** The badge palette, named as the `bg` prop of the shared `Badge` primitive. */
export type StatusVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'light'
  | 'dark';

/**
 * Status/stage → badge colour. THE single source of truth for status colour in the app.
 *
 * Every screen that shows a status used to carry its own copy of this mapping — twelve of them,
 * which is why the same task read `Active` in one colour on the task table and another in the
 * history panel, and why "Active - Marked as Complete" appeared as a green tick on one surface
 * and a blue badge on another. Render statuses through `<StatusBadge>` rather than reaching for
 * this map directly; it is exported for the few callers that need the raw variant.
 *
 * Keys cover the task lifecycle plus the history "stage" vocabularies of Work IDs, PCR
 * submissions and Projects, since those all render into the same badge.
 */
const STATUS_VARIANTS: Record<string, StatusVariant> = {
  // Task lifecycle
  PROPOSED: 'secondary',
  PLANNED: 'secondary',
  ACTIVE: 'primary',
  // Deliberately the same as ACTIVE: it *is* an active task, and colouring it as a completed
  // one is what made reviewers read it as finished.
  ACTIVE_MARKED_AS_COMPLETE: 'primary',
  INTERNAL_REVIEW: 'warning',
  CLOSED: 'dark',
  FINALIZED: 'info',
  CANCELLED: 'danger',
  INACTIVE: 'secondary',
  // Work ID lifecycle. These have no counterpart in the task vocabulary, so they are safe to
  // share; the two that DO collide are handled by WORK_ID_VARIANTS below.
  NEW: 'info',
  APPROVED: 'success',
  ASSIGNED: 'primary',
  // Shared history stages
  EFFORT_APPLIED: 'info',
  REOPENED: 'success',
  MANUAL_COMMENT: 'info',
  CREATED: 'primary',
  REPORTED: 'primary',
  DATA_EDIT: 'info',
  // Work ID history stages
  STATUS_CHANGE: 'warning',
  APPROVAL: 'success',
  ASSIGNMENT: 'primary',
  CANCELLATION: 'danger',
  MERGED: 'warning',
  CLOSED_WITHOUT_ACTION: 'dark',
  // PCR submission history stages
  PROMOTED_TO_WORK_ID: 'primary',
  CLOSED_WITHOUT_WORK_ID: 'dark',
  // Project history stages
  TASK_CREATED: 'primary',
  TASK_CANCELLED: 'danger',
  // Vendor catalogue history
  RESOURCE_ADDED: 'primary',
  RESOURCE_REMOVED: 'danger',
  RATE_ENTERED: 'success',
  RATE_REQUESTED: 'warning',
  RATE_APPROVED: 'success',
  RATE_REJECTED: 'danger',
  RATE_CANCELLED: 'secondary',
  RATE_WINDOW_UPDATED: 'info',
  DOCUMENT_ADDED: 'primary',
  DOCUMENT_UPDATED: 'info',
  DOCUMENT_REMOVED: 'danger',
  AREA_ADDED: 'primary',
  AVAILABILITY_CHANGE: 'warning',
  INSURANCE_UPLOADED: 'primary',
  INSURANCE_CLEARED: 'secondary',
};

/**
 * Overrides for the Work ID vocabulary, which shares two keys with the task lifecycle but not
 * their meaning: a CLOSED Work ID is a finished request (muted), where a CLOSED task is a
 * terminal cost state (dark). Rather than recolour a screen nobody reported as wrong, the two
 * readings live here side by side — and unlike the twelve local maps this replaced, the
 * divergence is now visible in one file instead of being discovered on a screenshot.
 *
 * Keep this as small as it can be: only add a key here when the shared reading is genuinely
 * wrong for Work IDs, not merely to preserve a historical accident.
 */
const WORK_ID_VARIANTS: Record<string, StatusVariant> = {
  CLOSED: 'secondary',
  CLOSED_WITHOUT_ACTION: 'secondary',
};

/**
 * The submission-approval vocabulary (Submissions / Approvals cards). A separate set of words
 * entirely — nothing here collides with a task stage, but they are kept in their own map rather
 * than merged so that "APPROVED" cannot be redefined for Work IDs by a change made for
 * submissions.
 *
 * Colours are as they shipped, including DECLINED reading muted rather than as an error; that is
 * a deliberate product choice about tone and is not this change's to revisit.
 */
const SUBMISSION_VARIANTS: Record<string, StatusVariant> = {
  APPROVED: 'success',
  PENDING: 'secondary',
  'NEED REVISIONS': 'danger',
  'REVISIONS COMPLETED': 'secondary',
  DECLINED: 'secondary',
};

/**
 * Vendor and project invoice statuses. `Finalized` is the collision here: an invoice reads it as
 * "issued, good to pay" (success), where a task reads it as a closed cost state (info).
 */
const INVOICE_VARIANTS: Record<string, StatusVariant> = {
  NEW: 'primary',
  FINALIZED: 'success',
};

/**
 * Project lifecycle. A project being ACTIVE is a healthy, in-flight state (success), where an
 * ACTIVE *task* is merely the normal working state (primary); and a COMPLETED project is a
 * result worth showing (primary) rather than a muted terminal row.
 */
const PROJECT_VARIANTS: Record<string, StatusVariant> = {
  ACTIVE: 'success',
  COMPLETED: 'primary',
  CANCELLED: 'danger',
};

/**
 * PCR submissions. Two collisions with the task lifecycle, both because a PCR is a *report being
 * triaged* rather than work being costed: INTERNAL_REVIEW is an informational waypoint here (info)
 * rather than a locked state (warning), and FINALIZED is the successful outcome (success) rather
 * than a closed cost state (info). NEW is the untriaged inbox state and wants attention.
 *
 * Note the PCR surfaces did not previously agree with each other either — the detail modal
 * defaulted unknown statuses to `success` where the two tables defaulted to `secondary`. This
 * takes the tables' reading, since defaulting an unrecognised status to "succeeded" is the more
 * dangerous of the two.
 */
const PCR_VARIANTS: Record<string, StatusVariant> = {
  NEW: 'warning',
  INTERNAL_REVIEW: 'info',
  FINALIZED: 'success',
  OUTBOUND_EMAIL: 'primary',
};

/** Which status vocabulary a value belongs to. Defaults to the task lifecycle. */
export type StatusVocabulary =
  | 'task'
  | 'workId'
  | 'submission'
  | 'invoice'
  | 'project'
  | 'pcr';

const VOCABULARIES: Record<string, Record<string, StatusVariant>> = {
  workId: WORK_ID_VARIANTS,
  submission: SUBMISSION_VARIANTS,
  invoice: INVOICE_VARIANTS,
  project: PROJECT_VARIANTS,
  pcr: PCR_VARIANTS,
};

/**
 * Badge colour for a status/stage, as a `Badge` `bg` value. Unknown values fall back to
 * `secondary` rather than throwing or rendering unstyled.
 */
export const getStatusVariant = (
  status: string,
  vocabulary: StatusVocabulary = 'task'
): StatusVariant => {
  const key = status?.toUpperCase() || '';
  // A vocabulary overrides only the keys it actually redefines; everything else falls through
  // to the shared reading, so adding one cannot fork the whole palette.
  return VOCABULARIES[vocabulary]?.[key] ?? STATUS_VARIANTS[key] ?? 'secondary';
};

/**
 * Get the badge class for a task status to match the colors used in task tables.
 *
 * Retained for callers still passing Bootstrap-shaped class names around (several do
 * `getStatusBadgeClass(x).replace('bg-', '')` to feed `Badge`). Prefer `<StatusBadge>`, or
 * `getStatusVariant` if you only need the colour.
 *
 * @param status - The task status/stage value
 * @returns The Bootstrap badge class name
 */
export const getStatusBadgeClass = (status: string): string => `bg-${getStatusVariant(status)}`;

